import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import CrossDocCompare from "../components/CrossDocCompare";
import { toast } from "sonner";

export default function ComparisonDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [comparison, setComparison] = useState(null);
  const [docA, setDocA] = useState(null);
  const [docB, setDocB] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refresh = async (c = comparison) => {
    if (!c) return;
    try {
      const [a, b] = await Promise.all([
        api.get(`/documents/${c.doc_a_id}`).then((r) => r.data),
        api.get(`/documents/${c.doc_b_id}`).then((r) => r.data),
      ]);
      setDocA(a);
      setDocB(b);
    } catch (e) {
      // silent — comparison still renders without the doc filenames
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/comparisons/${id}`);
        if (cancelled) return;
        setComparison(res.data);
        await refresh(res.data);
      } catch (e) {
        if (!cancelled) setError(formatApiErrorDetail(e.response?.data?.detail) || "Comparison not found");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (loading) {
    return (
      <div className="grid place-items-center py-24" data-testid="comparison-loading">
        <Loader2 size={20} className="animate-spin text-zinc-400" />
      </div>
    );
  }
  if (error || !comparison) {
    return (
      <div className="text-center py-20" data-testid="comparison-error">
        <p className="text-zinc-700 mb-3">{error || "Comparison not found"}</p>
        <Link to="/dashboard" className="text-zinc-950 underline">Back to dashboard</Link>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="comparison-detail-page">
      <button
        onClick={() => navigate(-1)}
        data-testid="comparison-back-button"
        className="flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-900 transition-colors"
      >
        <ArrowLeft size={14} /> Back
      </button>
      <CrossDocCompare
        comparison={comparison}
        docA={docA}
        docB={docB}
        onRefresh={(updated) => setComparison(updated)}
        onResolved={(updated) => setComparison(updated)}
      />
    </div>
  );
}
